import { useNavigate, Link } from 'react-router-dom';
import { Target, TrendingUp, Award, Video, BarChart3, BookOpen, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import NoDoublesLogo from './NoDoublesLogo';

export default function LandingPage() {
  const navigate = useNavigate();

  const handleGetStarted = () => {
    navigate('/auth');
  };

  const handleSignIn = () => {
    navigate('/auth');
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-[#FAFAFA]">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-gradient-to-r from-[#0D382A] to-[#196644] shadow-[0_2px_8px_rgba(13,56,42,0.12)]">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <NoDoublesLogo variant="white" size={56} className="cursor-pointer" />
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              onClick={handleSignIn}
              className="text-white hover:bg-white/10"
            >
              Sign In
            </Button>
            <Button 
              onClick={handleGetStarted}
              className="bg-[#F9C94E] text-[#0D382A] hover:bg-[#F9C94E]/90 shadow-[0_4px_12px_rgba(249,201,78,0.3)]"
            >
              Get Started
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="max-w-6xl mx-auto px-6 pt-12 pb-20 md:pt-16 md:pb-32">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <div>
            {/* Logo above AI-Powered */}
            <NoDoublesLogo variant="green" size={140} className="mb-6" />
            
            <p className="text-[#196644] font-semibold text-sm uppercase tracking-wide mb-3">
              AI-Powered Golf Improvement
            </p>
            <h1 className="text-4xl md:text-5xl font-bold text-[#0D382A] mb-6">
              Master Your Swing,<br />Lower Your Scores
            </h1>
            <p className="text-gray-600 text-lg mb-8">
              Upload your swing, get instant AI analysis, and follow personalized drills. 
              The smarter way to break through your scoring plateau.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button 
                onClick={handleGetStarted}
                size="lg"
                className="bg-gradient-to-r from-[#196644] to-[#0D382A] text-white hover:opacity-90 shadow-[0_4px_16px_rgba(13,56,42,0.25)]"
              >
                Join Free Beta
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button 
                variant="outline"
                size="lg"
                className="border-[#196644] text-[#196644] hover:bg-[#196644]/5"
              >
                Watch Demo
                <Video className="ml-2 h-5 w-5" />
              </Button>
            </div>
            
            {/* Trust Message */}
            <p className="mt-4 text-sm text-gray-600">
              No credit card required • Free to start
            </p>
          </div>
          
          <div className="relative">
            <div className="absolute -inset-2 bg-gradient-to-br from-[#196644]/20 to-[#F9C94E]/20 rounded-2xl blur-xl" />
            <div className="relative rounded-xl shadow-[0_4px_20px_rgba(13,56,42,0.15)] overflow-hidden aspect-[4/3] border border-[#196644]/10 bg-gradient-to-br from-green-50 to-yellow-50">
              <img 
                src="/lovable-uploads/herologo.png?v=2" 
                alt="No Doubles Golf logo with golf ball" 
                className="w-full h-full object-cover object-center"
                loading="lazy"
                onLoad={() => console.log('✅ Image loaded successfully: /lovable-uploads/herologo.png')}
                onError={(e) => {
                  console.error('❌ Image failed to load: /lovable-uploads/herologo.png');
                  console.error('Error details:', e);
                  console.error('Current URL:', window.location.href);
                  const target = e.target as HTMLImageElement;
                  console.error('Attempting to load from:', target.src);
                }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="bg-gradient-to-b from-[#FAFAFA] to-white py-20 md:py-32">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-16">
            <p className="text-[#196644] font-semibold text-sm uppercase tracking-wide mb-3">
              Simple & Effective
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-[#0D382A] mb-4">
              Your Daily Improvement Loop
            </h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              Three steps to measurable progress. No guesswork, no wasted practice time.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Step 1 */}
            <div className="bg-white rounded-2xl p-8 shadow-[0_2px_16px_rgba(13,56,42,0.08)] hover:shadow-[0_4px_24px_rgba(13,56,42,0.12)] transition-shadow">
              <div className="bg-gradient-to-br from-[#196644] to-[#0D382A] w-16 h-16 rounded-2xl flex items-center justify-center mb-6 shadow-[0_4px_12px_rgba(13,56,42,0.2)]">
                <Video className="h-8 w-8 text-white" />
              </div>
              <div className="flex items-center gap-3 mb-4">
                <span className="text-[#F9C94E] font-bold text-2xl">01</span>
                <h3 className="text-[#0D382A] font-semibold text-lg">Upload Your Swing</h3>
              </div>
              <p className="text-gray-600">
                Record from any angle. Our AI analyzes your mechanics in seconds.
              </p>
              <div className="mt-6 rounded-xl w-full aspect-video bg-gradient-to-br from-[#196644]/10 to-[#0D382A]/10 flex items-center justify-center shadow-[0_2px_8px_rgba(0,0,0,0.08)]">
                <p className="text-gray-400 text-xs">AI Analysis Image</p>
              </div>
            </div>

            {/* Step 2 */}
            <div className="bg-white rounded-2xl p-8 shadow-[0_2px_16px_rgba(13,56,42,0.08)] hover:shadow-[0_4px_24px_rgba(13,56,42,0.12)] transition-shadow">
              <div className="bg-gradient-to-br from-[#196644] to-[#0D382A] w-16 h-16 rounded-2xl flex items-center justify-center mb-6 shadow-[0_4px_12px_rgba(13,56,42,0.2)]">
                <Target className="h-8 w-8 text-white" />
              </div>
              <div className="flex items-center gap-3 mb-4">
                <span className="text-[#F9C94E] font-bold text-2xl">02</span>
                <h3 className="text-[#0D382A] font-semibold text-lg">Get Smart Drills</h3>
              </div>
              <p className="text-gray-600">
                Personalized practice plans targeting your biggest opportunities.
              </p>
              <div className="mt-6 rounded-xl w-full aspect-video bg-gradient-to-br from-[#196644]/10 to-[#0D382A]/10 flex items-center justify-center shadow-[0_2px_8px_rgba(0,0,0,0.08)]">
                <p className="text-gray-400 text-xs">Drill Practice Image</p>
              </div>
            </div>

            {/* Step 3 */}
            <div className="bg-white rounded-2xl p-8 shadow-[0_2px_16px_rgba(13,56,42,0.08)] hover:shadow-[0_4px_24px_rgba(13,56,42,0.12)] transition-shadow">
              <div className="bg-gradient-to-br from-[#196644] to-[#0D382A] w-16 h-16 rounded-2xl flex items-center justify-center mb-6 shadow-[0_4px_12px_rgba(13,56,42,0.2)]">
                <TrendingUp className="h-8 w-8 text-white" />
              </div>
              <div className="flex items-center gap-3 mb-4">
                <span className="text-[#F9C94E] font-bold text-2xl">03</span>
                <h3 className="text-[#0D382A] font-semibold text-lg">Track Progress</h3>
              </div>
              <p className="text-gray-600">
                See measurable improvements with data-driven insights and streaks.
              </p>
              <div className="mt-6 rounded-xl w-full aspect-video bg-gradient-to-br from-[#196644]/10 to-[#0D382A]/10 flex items-center justify-center shadow-[0_2px_8px_rgba(0,0,0,0.08)]">
                <p className="text-gray-400 text-xs">Progress Tracking Image</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-20 md:py-32">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-16">
            <p className="text-[#196644] font-semibold text-sm uppercase tracking-wide mb-3">
              Everything You Need
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-[#0D382A] mb-4">
              Built For Serious Improvement
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <FeatureCard
              icon={<Video className="h-6 w-6" />}
              title="AI Swing Analysis"
              description="Upload videos from any device. Get instant feedback on posture, tempo, and mechanics."
            />
            <FeatureCard
              icon={<Target className="h-6 w-6" />}
              title="Personalized Drills"
              description="Custom practice plans that adapt to your skill level and improvement areas."
            />
            <FeatureCard
              icon={<BarChart3 className="h-6 w-6" />}
              title="Progress Tracking"
              description="Visual dashboards showing your improvement over time with detailed metrics."
            />
            <FeatureCard
              icon={<Award className="h-6 w-6" />}
              title="Daily Challenges"
              description="Bite-sized goals to keep you motivated and build consistent practice habits."
            />
            <FeatureCard
              icon={<BookOpen className="h-6 w-6" />}
              title="Drill Library"
              description="100+ professional drills with video demonstrations and progression paths."
            />
            <FeatureCard
              icon={<TrendingUp className="h-6 w-6" />}
              title="Streak System"
              description="Build momentum with daily streaks. Track your commitment to improvement."
            />
          </div>
        </div>
      </section>

      {/* Beta Access */}
      <section className="bg-gradient-to-b from-white to-[#FAFAFA] py-20 md:py-32">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className="bg-gradient-to-br from-[#196644] to-[#0D382A] rounded-2xl p-10 md:p-16 shadow-[0_8px_32px_rgba(13,56,42,0.25)] relative overflow-hidden">
            {/* Background decoration */}
            <div className="absolute inset-0 opacity-10">
              <div className="absolute top-0 right-0 w-64 h-64 bg-[#F9C94E] rounded-full blur-3xl transform translate-x-1/2 -translate-y-1/2" />
              <div className="absolute bottom-0 left-0 w-64 h-64 bg-[#A7F3A1] rounded-full blur-3xl transform -translate-x-1/2 translate-y-1/2" />
            </div>
            
            {/* Beta badge */}
            <div className="inline-block bg-[#F9C94E] text-[#0D382A] px-4 py-1.5 rounded-full text-sm font-semibold mb-6 shadow-[0_2px_8px_rgba(249,201,78,0.3)]">
              Limited Beta Access
            </div>
            
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4 relative">
              Beta Phase is Free — Join Now
            </h2>
            
            <p className="text-white/80 text-lg mb-8 max-w-2xl mx-auto relative">
              Get early access to all features at no cost. Help shape the future of AI-powered golf coaching while improving your game.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center relative">
              <Button 
                size="lg"
                className="bg-[#F9C94E] text-[#0D382A] hover:bg-[#F9C94E]/90 shadow-[0_4px_12px_rgba(249,201,78,0.3)]"
                onClick={handleGetStarted}
              >
                Sign Up for Free Beta
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </div>
            
            <p className="text-white/60 text-sm mt-6 relative">
              No credit card required • Full access during beta
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-32">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-[#0D382A] mb-6">
            Ready to Lower Your Scores?
          </h2>
          <p className="text-gray-600 text-lg mb-8 max-w-2xl mx-auto">
            Join thousands of golfers using AI-powered coaching to break through their scoring plateau.
          </p>
          <Button 
            onClick={handleGetStarted}
            size="lg"
            className="bg-gradient-to-r from-[#196644] to-[#0D382A] text-white hover:opacity-90 shadow-[0_4px_16px_rgba(13,56,42,0.25)]"
          >
            Get Started Free
            <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gradient-to-r from-[#0D382A] to-[#196644] text-white">
        <div className="max-w-7xl mx-auto px-6 py-12">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <NoDoublesLogo variant="white" size={48} className="mb-4 mx-auto md:mx-0" />
              <p className="text-white/70 text-sm">The smarter way to lower your scores.</p>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Product</h4>
              <ul className="space-y-2 text-sm text-white/70">
                <li className="hover:text-white cursor-pointer">Features</li>
                <li className="hover:text-white cursor-pointer">How It Works</li>
                <li className="hover:text-white cursor-pointer">Beta Access</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Company</h4>
              <ul className="space-y-2 text-sm text-white/70">
                <li className="hover:text-white cursor-pointer">About</li>
                <li className="hover:text-white cursor-pointer">Blog</li>
                <li className="hover:text-white cursor-pointer">Contact</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Legal</h4>
              <ul className="space-y-2 text-sm text-white/70">
                <li><Link to="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link></li>
                <li><Link to="/terms" className="hover:text-white transition-colors">Terms of Service</Link></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-white/10 pt-8 text-center text-sm text-white/60">
            © 2025 No Doubles Golf Co. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
}

// Helper Components
function FeatureCard({ icon, title, description }: { icon: React.ReactNode; title: string; description: string }) {
  return (
    <div className="bg-white rounded-xl p-6 shadow-[0_2px_12px_rgba(13,56,42,0.06)] hover:shadow-[0_4px_20px_rgba(13,56,42,0.1)] transition-shadow">
      <div className="bg-gradient-to-br from-[#196644] to-[#0D382A] w-12 h-12 rounded-xl flex items-center justify-center mb-4 text-white shadow-[0_2px_8px_rgba(13,56,42,0.15)]">
        {icon}
      </div>
      <h3 className="text-[#0D382A] mb-2 text-lg font-semibold">{title}</h3>
      <p className="text-gray-600 text-sm">{description}</p>
    </div>
  );
}

